export type MobileInputSnapshot = {
  movePointer: number | null;
  lookPointer: number | null;
  firePointer: number | null;
  moveX: number;
  moveY: number;
  firing: boolean;
};

type MobileInputOptions = {
  root: HTMLElement;
  moveZone: HTMLElement;
  stickBase: HTMLElement;
  stickKnob: HTMLElement;
  lookZone: HTMLElement;
  fireButton: HTMLElement;
  deadzone?: number;
  radius?: number;
  onMove(x: number, forward: number): void;
  onLook(yawDelta: number, pitchDelta: number): void;
  onFire(): void;
  getSensitivity(): number;
};

type Point = { x: number; y: number };

/** Pointer Events mobile FPS adapter. Each action owns one pointer, so move/look/fire coexist. */
export class MobileInput {
  private readonly options: Required<Pick<MobileInputOptions, 'deadzone' | 'radius'>> & MobileInputOptions;
  private movePointer: number | null = null;
  private lookPointer: number | null = null;
  private firePointer: number | null = null;
  private moveOrigin: Point = { x: 0, y: 0 };
  private lookLast: Point = { x: 0, y: 0 };
  private moveX = 0;
  private moveY = 0;
  private frame = 0;
  private lastFrame = performance.now();
  private lastShot = -Infinity;
  private readonly disposers: Array<() => void> = [];

  constructor(options: MobileInputOptions) {
    this.options = { deadzone: 0.16, radius: 52, ...options };
    this.bindMove();
    this.bindLook();
    this.bindFire();
    const recover = () => this.cancelAll();
    window.addEventListener('blur', recover);
    document.addEventListener('visibilitychange', recover);
    this.disposers.push(() => window.removeEventListener('blur', recover));
    this.disposers.push(() => document.removeEventListener('visibilitychange', recover));
    this.frame = requestAnimationFrame(this.tick);
    this.publish();
  }

  snapshot(): MobileInputSnapshot {
    return { movePointer: this.movePointer, lookPointer: this.lookPointer, firePointer: this.firePointer, moveX: this.moveX, moveY: this.moveY, firing: this.firePointer !== null };
  }

  cancelAll() {
    this.releaseMove();
    this.releaseLook();
    this.releaseFire();
  }

  dispose() {
    this.cancelAll();
    cancelAnimationFrame(this.frame);
    this.disposers.splice(0).forEach(dispose => dispose());
  }

  private listen(element: EventTarget, type: string, handler: EventListener) {
    element.addEventListener(type, handler, { passive: false });
    this.disposers.push(() => element.removeEventListener(type, handler));
  }

  private capture(element: HTMLElement, event: PointerEvent) {
    try { element.setPointerCapture(event.pointerId); } catch { /* Pointer may already be gone. */ }
  }

  private releaseCapture(element: HTMLElement, pointerId: number) {
    try { if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId); } catch { /* Recovery must be idempotent. */ }
  }

  private bindMove() {
    const { moveZone } = this.options;
    this.listen(moveZone, 'pointerdown', ((event: PointerEvent) => {
      if (event.pointerType === 'mouse' || this.movePointer !== null) return;
      event.preventDefault();
      this.movePointer = event.pointerId;
      this.moveOrigin = { x: event.clientX, y: event.clientY };
      this.options.stickBase.style.left = `${event.clientX - moveZone.getBoundingClientRect().left}px`;
      this.options.stickBase.style.top = `${event.clientY - moveZone.getBoundingClientRect().top}px`;
      this.options.stickBase.classList.add('active');
      this.capture(moveZone, event);
      this.updateMove(event);
    }) as EventListener);
    this.listen(moveZone, 'pointermove', ((event: PointerEvent) => {
      if (event.pointerId !== this.movePointer) return;
      event.preventDefault();
      this.updateMove(event);
    }) as EventListener);
    const end = (event: PointerEvent) => {
      if (event.pointerId !== this.movePointer) return;
      event.preventDefault();
      this.releaseMove();
    };
    this.listen(moveZone, 'pointerup', end as EventListener);
    this.listen(moveZone, 'pointercancel', end as EventListener);
    this.listen(moveZone, 'lostpointercapture', end as EventListener);
  }

  private updateMove(event: PointerEvent) {
    const dx = event.clientX - this.moveOrigin.x;
    const dy = event.clientY - this.moveOrigin.y;
    const distance = Math.hypot(dx, dy);
    const clamped = Math.min(distance, this.options.radius);
    const nx = distance ? dx / distance : 0;
    const ny = distance ? dy / distance : 0;
    const rawMagnitude = clamped / this.options.radius;
    const magnitude = rawMagnitude <= this.options.deadzone ? 0 : (rawMagnitude - this.options.deadzone) / (1 - this.options.deadzone);
    this.moveX = nx * magnitude;
    this.moveY = ny * magnitude;
    this.options.stickKnob.style.transform = `translate(calc(-50% + ${nx * clamped}px),calc(-50% + ${ny * clamped}px))`;
    this.publish();
  }

  private releaseMove() {
    if (this.movePointer !== null) this.releaseCapture(this.options.moveZone, this.movePointer);
    this.movePointer = null;
    this.moveX = 0;
    this.moveY = 0;
    this.options.stickBase.classList.remove('active');
    this.options.stickKnob.style.transform = 'translate(-50%,-50%)';
    this.publish();
  }

  private bindLook() {
    const { lookZone } = this.options;
    this.listen(lookZone, 'pointerdown', ((event: PointerEvent) => {
      if (event.pointerType === 'mouse' || this.lookPointer !== null) return;
      event.preventDefault();
      this.lookPointer = event.pointerId;
      this.lookLast = { x: event.clientX, y: event.clientY };
      this.capture(lookZone, event);
      lookZone.classList.add('active');
      this.publish();
    }) as EventListener);
    this.listen(lookZone, 'pointermove', ((event: PointerEvent) => {
      if (event.pointerId !== this.lookPointer) return;
      event.preventDefault();
      const sensitivity = this.options.getSensitivity();
      const dx = event.clientX - this.lookLast.x;
      const dy = event.clientY - this.lookLast.y;
      this.lookLast = { x: event.clientX, y: event.clientY };
      this.options.onLook(dx * 0.0042 * sensitivity, -dy * 0.0034 * sensitivity);
    }) as EventListener);
    const end = (event: PointerEvent) => {
      if (event.pointerId !== this.lookPointer) return;
      event.preventDefault();
      this.releaseLook();
    };
    this.listen(lookZone, 'pointerup', end as EventListener);
    this.listen(lookZone, 'pointercancel', end as EventListener);
    this.listen(lookZone, 'lostpointercapture', end as EventListener);
  }

  private releaseLook() {
    if (this.lookPointer !== null) this.releaseCapture(this.options.lookZone, this.lookPointer);
    this.lookPointer = null;
    this.options.lookZone.classList.remove('active');
    this.publish();
  }

  private bindFire() {
    const { fireButton } = this.options;
    this.listen(fireButton, 'pointerdown', ((event: PointerEvent) => {
      if (event.pointerType === 'mouse' || this.firePointer !== null) return;
      event.preventDefault();
      this.firePointer = event.pointerId;
      this.capture(fireButton, event);
      fireButton.classList.add('active');
      this.lastShot = performance.now();
      this.options.onFire();
      this.publish();
    }) as EventListener);
    const end = (event: PointerEvent) => {
      if (event.pointerId !== this.firePointer) return;
      event.preventDefault();
      this.releaseFire();
    };
    this.listen(fireButton, 'pointerup', end as EventListener);
    this.listen(fireButton, 'pointercancel', end as EventListener);
    this.listen(fireButton, 'lostpointercapture', end as EventListener);
  }

  private releaseFire() {
    if (this.firePointer !== null) this.releaseCapture(this.options.fireButton, this.firePointer);
    this.firePointer = null;
    this.options.fireButton.classList.remove('active');
    this.publish();
  }

  private readonly tick = (now: number) => {
    const dt = Math.min(34, now - this.lastFrame);
    this.lastFrame = now;
    if (this.movePointer !== null && (this.moveX || this.moveY)) this.options.onMove(this.moveX * dt / 16.667, -this.moveY * dt / 16.667);
    if (this.firePointer !== null && now - this.lastShot >= 170) {
      this.lastShot = now;
      this.options.onFire();
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  private publish() {
    const state = this.snapshot();
    this.options.root.dataset.mobileMove = `${state.moveX.toFixed(3)},${state.moveY.toFixed(3)}`;
    this.options.root.dataset.mobileLookActive = `${state.lookPointer !== null}`;
    this.options.root.dataset.mobileFiring = `${state.firing}`;
  }
}
